/*
  Project Structure for BMS projects (final QA, screen 08 ideas 1 and 9): the same two-pane editor as KNX / LCS.
  - bmsStructureNodes(structure)  the tree: buildings › subs › sub-items that hold sub-items of their own
  - BmsBuildingBody               a building's subs, each with its type, zone and how many sub-items it holds
  - BmsNodeBody                   a sub / sub-item: Type · Zone · Controlled by, then its sub-items as a grid
  - BmsSubItemGrid                read-first rows (text; Edit or a double-click edits one row); "Edit all" is the
                                  keyboard entry grid of the old editor (↑ ↓ move to the same field one row up / down)
  Everything that changes the structure stays in the page (ops.bms); this file draws it.
*/
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, ClipboardPaste, Copy, CopyPlus, GripVertical, Keyboard, Pencil,
  Plus, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { StructureKindIcon } from './StructurePanels';
import { getBmsSubCollapseKey, getBuildingCollapseKey, getFloorCollapseKey } from './structureModel';
import { countNodeDevices } from './projectDetailModel';

const text = (value) => String(value ?? '').trim();
const list = (node, key) => (Array.isArray(node?.[key]) ? node[key] : []);
const fmt = (value) => formatNumber(value, { decimals: 0 });
const ROW_STEP = 60;

/** A BMS node's own sub-items (nested ones keep them in `children`, older data in `devices`). */
export const bmsChildrenOf = (node) => {
  if (!node || typeof node !== 'object') return [];
  if (Array.isArray(node.children)) return node.children;
  if (Array.isArray(node.devices)) return node.devices;
  return [];
};

/**
 * Devices below a building / sub / sub-item — counted like the summary strip (countNodeDevices: panels and other
 * containers are not devices), so the tree, the overview and the strip agree.
 */
export function countBmsItems(node, kind = 'item') {
  if (kind === 'building' || kind === 'sub') return countNodeDevices(node);
  return countNodeDevices({ devices: bmsChildrenOf(node) });
}

const nodeLabel = (node, fallback) => text(node?.name) || text(node?.part_number) || fallback;

/**
 * The tree of a BMS structure, parents first: { id, kind ('building' | 'sub' | 'bmsNode'), node, name, location
 * ({ buildingId, floorId, path }), parentId, path (names), depth, collapseKey }. Sub-items without sub-items of
 * their own are not in the tree — they are rows of their parent's grid.
 */
export function bmsStructureNodes(structure = {}) {
  const nodes = [];
  list(structure, 'buildings').forEach((building) => {
    const bName = text(building.name) || 'Building';
    nodes.push({ id: building.id, kind: 'building', node: building, name: bName, location: { buildingId: building.id }, parentId: null, path: [], depth: 0, collapseKey: getBuildingCollapseKey(building.id) });
    list(building, 'floors').forEach((floor) => {
      const fName = nodeLabel(floor, 'Sub');
      nodes.push({ id: floor.id, kind: 'sub', node: floor, name: fName, location: { buildingId: building.id, floorId: floor.id, path: [] }, parentId: building.id, path: [bName], depth: 1, collapseKey: getFloorCollapseKey(building.id, floor.id) });
      const walk = (items, parentId, idPath, names, depth) => items.forEach((item) => {
        const kids = bmsChildrenOf(item);
        if (!kids.length) return;
        const nodePath = [...idPath, item.id];
        const name = nodeLabel(item, 'Sub-item');
        nodes.push({ id: item.id, kind: 'bmsNode', node: item, name, location: { buildingId: building.id, floorId: floor.id, path: nodePath }, parentId, path: names, depth, collapseKey: getBmsSubCollapseKey(building.id, floor.id, nodePath) });
        walk(kids, item.id, nodePath, [...names, name], depth + 1);
      });
      walk(list(floor, 'devices'), floor.id, [], [bName, fName], 2);
    });
  });
  return nodes;
}

/** The type field of a sub ('sub_type') or a nested sub-item ('device_type'). */
export const bmsTypeField = (kind) => (kind === 'sub' ? 'sub_type' : 'device_type');

/* ------------------------------------------------------------------ type picker */

/** Sub type: a list to pick from (search above 8), and "Add …" for a type the list does not have yet. */
export function BmsTypePicker({ value, options = [], onChange, onCreate, disabled = false, label = 'Sub type', className }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shown = needle ? options.filter((option) => option.toLowerCase().includes(needle)) : options;
  const canCreate = Boolean(onCreate && needle && !options.some((option) => option.toLowerCase() === needle));
  const pick = (next) => {
    setOpen(false);
    setQuery('');
    if (next !== value) onChange(next);
  };
  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${label}: ${value || 'none'}`}
          className={cn(
            'flex h-8 min-w-0 items-center justify-between gap-1.5 rounded-lg border border-line bg-surface px-2.5 text-left text-[13px] text-ink',
            'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
            className
          )}
        >
          <span className={cn('truncate', !value && 'text-ink-3')}>{value || 'Type'}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[240px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        {options.length > 8 || onCreate ? (
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (shown[0]) pick(shown[0]);
                else if (canCreate) pick(onCreate(query.trim()));
              }
            }}
            placeholder={onCreate ? 'Find or add a type…' : 'Find a type…'}
            aria-label={`Find ${label.toLowerCase()}`}
            className="mb-1 h-8 rounded-lg text-[13px]"
            autoFocus
          />
        ) : null}
        <ul className="max-h-[240px] overflow-y-auto" role="listbox" aria-label={label}>
          {shown.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === value}
                onClick={() => pick(option)}
                className={cn('flex h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-[13px] hover:bg-surface-hover', option === value && 'font-semibold')}
              >
                <span className="min-w-0 flex-1 truncate">{option}</span>
                {option === value ? <Check className="h-3.5 w-3.5 text-brand-ink" aria-hidden="true" /> : null}
              </button>
            </li>
          ))}
          {canCreate ? (
            <li>
              <button type="button" onClick={() => pick(onCreate(query.trim()))} className="flex h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-[13px] font-semibold text-brand-ink hover:bg-surface-hover">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add “{query.trim()}”
              </button>
            </li>
          ) : null}
          {!shown.length && !canCreate ? <li className="px-2 py-2 text-[12.5px] text-ink-3">No type matches.</li> : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ building */

/** A BMS building in the right pane: its subs (type · zone · sub-items), each opens. */
export function BmsBuildingBody({ entry, kids, onSelect, canEdit, onAddSub }) {
  return (
    <section className="space-y-2">
      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Subs · {kids.length}</p>
      {kids.length ? (
        <ul className="divide-y divide-line-2 overflow-hidden rounded-xl border border-line">
          {kids.map((child) => {
            const items = countBmsItems(child.node, 'sub');
            const meta = [text(child.node.sub_type), text(child.node.zone_name), text(child.node.controlled_by) ? `by ${text(child.node.controlled_by)}` : ''].filter(Boolean).join(' · ');
            return (
              <li key={child.id}>
                <button type="button" onClick={() => onSelect(child)} className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover" data-testid={`structure-open-${child.id}`}>
                  <StructureKindIcon kind="sub" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-ink">{child.name}</span>
                    <span className="block truncate text-[12px] text-ink-3">{meta || 'Sub'}</span>
                  </span>
                  <span className="text-[12.5px] tabular-nums text-ink-2">{fmt(items)} device{items === 1 ? '' : 's'}</span>
                  <ChevronRight className="h-4 w-4 text-ink-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="rounded-xl border border-dashed border-[color:var(--line-dashed)] px-4 py-5 text-center text-[13px] text-ink-3">
          No subs in {entry.name} yet.
          {canEdit ? (
            <Button type="button" variant="outline" size="sm" className="ml-3" onClick={onAddSub}>
              <Plus aria-hidden="true" />
              Add sub
            </Button>
          ) : null}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ a sub / sub-item */

const FIELDS = [
  { key: 'name', label: 'Name / ID' },
  { key: 'zone_name', label: 'Zone' },
  { key: 'controlled_by', label: 'Controlled by' },
];

/** Type · Zone · Controlled by of the chosen sub / sub-item: text, "Edit details" turns them into fields. */
function BmsNodeFacts({ entry, ops }) {
  const [editing, setEditing] = useState(false);
  const typeField = bmsTypeField(entry.kind);
  const facts = [
    { key: typeField, label: 'Type', value: text(entry.node[typeField]) },
    { key: 'zone_name', label: 'Zone', value: text(entry.node.zone_name) },
    { key: 'controlled_by', label: 'Controlled by', value: text(entry.node.controlled_by) },
  ];
  if (editing) {
    return (
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-surface-2 px-3 py-2.5" data-testid="structure-bms-facts-edit">
        <label className="space-y-1">
          <span className="block text-[11.5px] font-semibold text-ink-3">Type</span>
          <BmsTypePicker
            value={text(entry.node[typeField])}
            options={ops.bms.typeOptions}
            onChange={(value) => ops.bms.changeSelf(entry, typeField, value)}
            onCreate={ops.bms.createType}
            className="w-44"
          />
        </label>
        {FIELDS.slice(1).map((field) => (
          <label key={field.key} className="space-y-1">
            <span className="block text-[11.5px] font-semibold text-ink-3">{field.label}</span>
            <Input
              value={entry.node[field.key] || ''}
              onChange={(event) => ops.bms.changeSelf(entry, field.key, event.target.value)}
              aria-label={`${field.label} of ${entry.name}`}
              className="h-8 w-48 rounded-lg text-[13px]"
            />
          </label>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)}>
          <Check aria-hidden="true" />
          Done
        </Button>
      </div>
    );
  }
  return (
    <dl className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[13px]" data-testid="structure-bms-facts">
      {facts.map((fact) => (
        <div key={fact.key} className="flex items-baseline gap-1.5">
          <dt className="text-[12px] text-ink-3">{fact.label}</dt>
          <dd className={cn('font-semibold', fact.value ? 'text-ink' : 'font-normal text-ink-4')}>{fact.value || '—'}</dd>
        </div>
      ))}
      {ops.canEdit ? (
        <Button type="button" variant="ghost" size="xs" onClick={() => setEditing(true)} data-testid="structure-bms-edit-facts">
          <Pencil aria-hidden="true" />
          Edit details
        </Button>
      ) : null}
    </dl>
  );
}

/** The right pane under the header for a sub / sub-item: its facts and its sub-items. */
export function BmsNodeBody({ entry, ops, onOpen, openIds }) {
  return (
    <div className="space-y-4">
      <BmsNodeFacts key={entry.id} entry={entry} ops={ops} />
      <BmsSubItemGrid key={`grid-${entry.id}`} entry={entry} ops={ops} onOpen={onOpen} openIds={openIds} />
    </div>
  );
}

/* ------------------------------------------------------------------ the grid */

const GRID = 'md:grid-cols-[20px_minmax(0,0.85fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_5.5rem_2rem]';
const GRID_DRAG = 'md:grid-cols-[20px_28px_minmax(0,0.85fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_5.5rem_2rem]';
// phones: check box · name with type / zone / controlled by under it · ⋯
const PHONE = 'max-md:grid-cols-[20px_minmax(0,1fr)_auto_auto] max-md:py-1.5';
// 28 #8: a View-only role has nothing to select (the selection only feeds Copy / Duplicate / Move / Delete)
const GRID_READ = 'md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_5.5rem_2rem]';
const PHONE_READ = 'max-md:grid-cols-[minmax(0,1fr)_auto_auto] max-md:py-1.5';

function CellText({ value, mono = false, onEdit, title }) {
  return (
    <span
      className={cn('flex h-8 min-w-0 items-center truncate rounded-md px-1 text-[13px] text-ink-2', mono && 'font-mono text-[12.5px]', onEdit && 'cursor-text hover:bg-surface-hover')}
      onDoubleClick={onEdit}
      title={value ? `${title}: ${value}${onEdit ? ' — double-click to edit' : ''}` : undefined}
    >
      {value || <span className="text-ink-4" aria-label={`No ${title.toLowerCase()}`}>—</span>}
    </span>
  );
}

/**
 * The sub-items of a sub / sub-item. Read-first: text cells; "Edit" in a row's ⋯ (or a double-click on a value)
 * edits that row; "Edit all" turns every row into fields (the old keyboard entry grid: ↑ ↓ move between rows).
 * Selection toolbar: Copy · Duplicate · Move up / down · Delete…; Paste puts copied sub-items at the end.
 */
export function BmsSubItemGrid({ entry, ops, onOpen, openIds = new Set() }) {
  const bms = ops.bms;
  const items = bms.itemsOf(entry);
  const canEdit = ops.canEdit;
  const [entryMode, setEntryMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [focusField, setFocusField] = useState('name');
  const [limit, setLimit] = useState(ROW_STEP);
  const fieldRefs = useRef(new Map());
  const selected = bms.selected(entry);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const typeField = 'device_type';
  const dragMode = bms.dragMode && canEdit;

  // a row that starts editing gets its field focused (the new row after "Add sub-item" too)
  useEffect(() => {
    if (!editingId) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const field = fieldRefs.current.get(`${editingId}:${focusField}`);
      field?.focus();
      field?.select?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editingId, focusField]);

  const shown = entryMode ? items : items.slice(0, limit);
  const add = () => {
    const newId = bms.add(entry);
    if (newId && !entryMode) { setFocusField('name'); setEditingId(newId); }
    if (items.length >= limit) setLimit(items.length + 1);
  };
  const moveFocus = (event, index, field) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
    const next = shown[index + (event.key === 'ArrowDown' ? 1 : -1)];
    if (!next) return;
    event.preventDefault();
    const target = fieldRefs.current.get(`${next.id}:${field}`);
    target?.focus();
    target?.select?.();
  };
  const register = (key) => (node) => {
    if (node) fieldRefs.current.set(key, node);
    else fieldRefs.current.delete(key);
  };

  return (
    <section className="space-y-2" data-testid={`structure-bms-grid-${entry.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Sub-items · {fmt(items.length)}</p>
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {bms.clipboardCount ? (
              <Button type="button" variant="ghost" size="xs" onClick={() => bms.paste(entry)} data-testid="structure-bms-paste">
                <ClipboardPaste aria-hidden="true" />
                Paste {bms.clipboardCount}
              </Button>
            ) : null}
            {items.length ? (
              <Button
                type="button"
                variant={entryMode ? 'outline' : 'ghost'}
                size="xs"
                aria-pressed={entryMode}
                onClick={() => { setEditingId(null); setEntryMode(!entryMode); }}
                title="Every row becomes fields; ↑ ↓ move to the same field one row up or down"
                className={entryMode ? 'border-brand-soft-border bg-brand-soft text-brand-ink' : undefined}
                data-testid="structure-bms-entry-mode"
              >
                <Keyboard aria-hidden="true" />
                {entryMode ? 'Done editing' : 'Edit all'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {selected.length && canEdit ? (
        <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-[color:var(--brand-soft-border)] bg-brand-soft px-3 py-1.5 text-[12.5px]" data-testid="structure-bms-selection">
          <span className="mr-1 font-semibold text-ink">{selected.length} selected</span>
          <Button type="button" variant="ghost" size="xs" onClick={() => bms.copy(entry, items)}><Copy aria-hidden="true" />Copy</Button>
          <Button type="button" variant="ghost" size="xs" onClick={() => bms.duplicateSelected(entry, items)}><CopyPlus aria-hidden="true" />Duplicate</Button>
          <Button type="button" variant="ghost" size="xs" onClick={() => bms.move(entry, 'up')} aria-label="Move the selected sub-items up"><ArrowUp aria-hidden="true" />Up</Button>
          <Button type="button" variant="ghost" size="xs" onClick={() => bms.move(entry, 'down')} aria-label="Move the selected sub-items down"><ArrowDown aria-hidden="true" />Down</Button>
          <Button type="button" variant="ghost" size="xs" className="text-status-red-ink" onClick={() => bms.deleteSelected(entry)}><Trash2 aria-hidden="true" />Delete…</Button>
          <button type="button" className="ml-auto text-[12px] font-semibold text-ink-3 hover:text-ink" onClick={() => bms.toggleAll(entry, items, false)}>Clear</button>
        </div>
      ) : null}

      {items.length ? (
        <div className="overflow-hidden rounded-xl border border-line">
          <div className={cn('hidden items-center gap-2 border-b border-line bg-surface-2 px-3 py-2 text-[11.5px] font-bold text-ink-3 md:grid', !canEdit ? GRID_READ : dragMode ? GRID_DRAG : GRID)}>
            {canEdit ? (
              <TableCheckbox
                checked={selected.length > 0 && selected.length === items.length}
                indeterminate={selected.length > 0 && selected.length < items.length}
                onCheckedChange={(checked) => bms.toggleAll(entry, items, Boolean(checked))}
                aria-label={`Select all sub-items of ${entry.name}`}
              />
            ) : null}
            {dragMode ? <span /> : null}
            <span>Type</span>
            <span>Name / ID</span>
            <span>Zone</span>
            <span>Controlled by</span>
            <span className="text-right">Sub-items</span>
            <span />
          </div>
          <div
            className="divide-y divide-line-2"
            onDragOver={(event) => { if (dragMode && bms.dragging) event.preventDefault(); }}
            onDrop={(event) => { if (!dragMode || !bms.dragging) return; event.preventDefault(); bms.dropInto(entry, null); }}
          >
            {shown.map((item, index) => {
              const editing = entryMode || editingId === item.id;
              const kids = bmsChildrenOf(item).length;
              const label = nodeLabel(item, 'sub-item');
              const startEdit = canEdit && !entryMode ? (field) => () => { setFocusField(field); setEditingId(item.id); } : () => undefined;
              const finishRow = () => { if (!entryMode) setEditingId(null); };
              return (
                <div
                  key={item.id}
                  data-structure-node={item.id}
                  tabIndex={-1}
                  className={cn(
                    'group/row grid items-center gap-2 px-3 py-1 outline-none',
                    !canEdit ? GRID_READ : dragMode ? GRID_DRAG : GRID,
                    !editing && (canEdit ? PHONE : PHONE_READ),
                    selectedSet.has(item.id) ? 'bg-brand-soft' : 'hover:bg-surface-hover',
                    editing && !entryMode && 'bg-surface-2',
                    bms.dragging?.deviceId === item.id && 'opacity-40',
                    ops.focusClass(item.id)
                  )}
                  onBlur={(event) => {
                    if (editingId === item.id && !entryMode && !event.currentTarget.contains(event.relatedTarget) && !event.relatedTarget?.closest?.('[data-radix-popper-content-wrapper]')) finishRow();
                  }}
                  onDragOver={(event) => { if (dragMode && bms.dragging) event.preventDefault(); }}
                  onDrop={(event) => {
                    if (!dragMode || !bms.dragging) return;
                    event.preventDefault();
                    event.stopPropagation();
                    bms.dropInto(entry, item);
                  }}
                  data-testid={`structure-bms-row-${item.id}`}
                >
                  {canEdit ? (
                    <span data-bms-cell="chk" className="flex items-center">
                      <TableCheckbox
                        checked={selectedSet.has(item.id)}
                        onCheckedChange={(checked) => bms.toggle(entry, item.id, Boolean(checked))}
                        aria-label={`Select ${label}`}
                      />
                    </span>
                  ) : null}
                  {dragMode ? (
                    <button
                      type="button"
                      draggable
                      onDragStart={() => bms.dragStart(entry, item)}
                      onDragEnd={bms.dragEnd}
                      className="grid h-7 w-7 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink max-md:hidden"
                      title="Drag onto another sub-item to move it inside, or onto a sub in the tree"
                      aria-label={`Drag ${label}`}
                    >
                      <GripVertical className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ) : null}
                  {editing ? (
                    <>
                      <BmsTypePicker
                        value={text(item[typeField])}
                        options={bms.typeOptions}
                        onChange={(value) => bms.change(entry, item, typeField, value)}
                        onCreate={bms.createType}
                        label={`Type of ${label}`}
                        className="w-full"
                      />
                      {FIELDS.map((field) => (
                        <Input
                          key={field.key}
                          ref={register(`${item.id}:${field.key}`)}
                          value={item[field.key] || ''}
                          onChange={(event) => bms.change(entry, item, field.key, event.target.value)}
                          onKeyDown={(event) => {
                            moveFocus(event, index, field.key);
                            if (!entryMode && (event.key === 'Enter' || event.key === 'Escape')) { event.preventDefault(); finishRow(); }
                          }}
                          placeholder={field.label}
                          aria-label={`${field.label} of ${label}`}
                          className="h-8 rounded-lg text-[13px]"
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      <span className="max-md:hidden">
                        <CellText value={text(item[typeField])} title="Type" onEdit={canEdit ? startEdit('name') : undefined} />
                      </span>
                      <span data-bms-cell="name" className="min-w-0">
                        <span
                          className={cn('flex h-8 min-w-0 items-center truncate rounded-md px-1 text-[13px] font-semibold text-ink', canEdit && 'cursor-text hover:bg-surface-hover')}
                          onDoubleClick={canEdit ? startEdit('name') : undefined}
                          title={canEdit ? 'Double-click to edit' : undefined}
                        >
                          {text(item.name) || <span className="font-normal text-ink-4">Untitled</span>}
                        </span>
                        <span className="block truncate px-1 text-[12px] text-ink-3 md:hidden">
                          {[text(item[typeField]), text(item.zone_name), text(item.controlled_by) ? `by ${text(item.controlled_by)}` : ''].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </span>
                      <span className="max-md:hidden"><CellText value={text(item.zone_name)} title="Zone" onEdit={canEdit ? startEdit('zone_name') : undefined} /></span>
                      <span className="max-md:hidden"><CellText value={text(item.controlled_by)} title="Controlled by" onEdit={canEdit ? startEdit('controlled_by') : undefined} /></span>
                    </>
                  )}
                  <span className="flex justify-end text-[12.5px] tabular-nums">
                    {kids ? (
                      <button
                        type="button"
                        onClick={() => onOpen(item.id)}
                        className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold text-brand-ink hover:bg-surface-hover"
                        aria-label={`Open ${label} (${kids} sub-items)`}
                        disabled={!openIds.has(item.id)}
                      >
                        {fmt(kids)}
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    ) : <span className="px-1.5 text-ink-4">—</span>}
                  </span>
                  <span data-bms-cell="act" className="flex justify-end">
                    {editing && !entryMode ? (
                      <Button type="button" variant="outline" size="icon-sm" onClick={finishRow} aria-label={`Done editing ${label}`}>
                        <Check aria-hidden="true" />
                      </Button>
                    ) : (
                      <RowActions
                        label={`Actions for ${label}`}
                        className="opacity-60 group-hover/row:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                        items={[
                          kids ? { label: `Open (${kids} sub-items)`, icon: ChevronRight, onSelect: () => onOpen(item.id) } : null,
                          canEdit && !entryMode ? { label: 'Edit', icon: Pencil, onSelect: () => { setFocusField('name'); setEditingId(item.id); } } : null,
                          canEdit ? { label: 'Add sub-item inside', icon: Plus, onSelect: () => bms.addInside(entry, item) } : null,
                          canEdit ? { label: 'Duplicate', icon: CopyPlus, onSelect: () => bms.duplicate(entry, item) } : null,
                          canEdit ? { label: 'Delete sub-item…', icon: Trash2, danger: true, onSelect: () => bms.remove(entry, item) } : null,
                        ]}
                      />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-line bg-surface-2 px-3 py-2 text-[12.5px] text-ink-3">
            <span>Showing {fmt(shown.length)} of {fmt(items.length)}</span>
            <span className="flex items-center gap-3">
              {items.length > shown.length ? (
                <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => setLimit(items.length)}>Show all</button>
              ) : null}
              {canEdit ? (
                <button type="button" className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline" onClick={add} data-testid="structure-bms-add">
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add sub-item
                </button>
              ) : null}
            </span>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[color:var(--line-dashed)] px-4 py-5 text-center text-[13px] text-ink-3">
          No sub-items here yet.
          {canEdit ? (
            <Button type="button" variant="outline" size="sm" className="ml-3" onClick={add} data-testid="structure-bms-add">
              <Plus aria-hidden="true" />
              Add sub-item
            </Button>
          ) : null}
        </div>
      )}
    </section>
  );
}
